import { Badge } from '../components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { HelpCircle, Mail } from 'lucide-react';

const faqs = [
  {
    category: 'About the Hub',
    questions: [
      {
        q: 'What is the Autism Acceptance Resources Hub?',
        a: 'The Hub is a central online resource for the autism community in the Cayman Islands. It provides information about autism, a directory of local services, downloadable tools and guides, a community forum, event listings, and more, all from a neurodiversity-affirming perspective.'
      },
      {
        q: 'Who created this Hub?',
        a: 'The Hub was created by Autism Acceptance, a registered non-profit organisation (NP-693) in the Cayman Islands. Our board consists entirely of autism parents who wanted to create the resource they wished existed when they started their own journeys.'
      },
      {
        q: 'Is this Hub free to use?',
        a: 'Yes! The Hub is completely free for all users. We are a non-profit organisation funded by donations and community support.'
      },
      {
        q: 'How can I support the Hub?',
        a: 'You can support us by donating at donate.autismacceptance.ky, volunteering your time, spreading awareness about our resources, and using and sharing our materials.'
      }
    ]
  },
  {
    category: 'Account & Registration',
    questions: [
      {
        q: 'Do I need an account to use the Hub?',
        a: 'You can browse most content without an account. However, creating an account allows you to save favourites, participate in the community forum, register for events, and receive our newsletter.'
      },
      {
        q: 'How do I reset my password?',
        a: 'Click "Forgot Password" on the login page and enter your email address. You will receive instructions to reset your password.'
      },
      {
        q: 'Is my information kept private?',
        a: 'Yes. We take data protection seriously and comply with all applicable data protection laws. Your personal information is never shared without your consent. See our Privacy Policy for details.'
      },
      {
        q: 'Can I post anonymously in the forum?',
        a: 'Yes! When creating a post or comment, you can choose to post anonymously. Your identity will be hidden from other users, though administrators can see who posted for moderation purposes.'
      }
    ]
  },
  {
    category: 'Services & Resources',
    questions: [
      {
        q: 'How are services in the directory vetted?',
        a: 'We aim to include services that are neuro-affirming. However, we recommend doing your own research and asking questions before engaging any service. Autism Acceptance does not endorse or guarantee any specific provider.'
      },
      {
        q: 'Can I suggest a service to add to the directory?',
        a: 'Yes! Contact us at admin@autismacceptance.ky with details about the service you would like to recommend.'
      },
      {
        q: 'Are the downloadable resources free to use?',
        a: 'Yes, all resources are free to download and use. Materials are copyright Autism Acceptance 2026 but may be used for distribution and educational purposes.'
      }
    ]
  },
  {
    category: 'Events & Community',
    questions: [
      {
        q: 'How do I register for events?',
        a: 'Click the "RSVP" button on any event listing and fill out the registration form with details for all attendees, including any support needs or allergies.'
      },
      {
        q: 'Will I receive a reminder before events?',
        a: 'Yes, registered attendees receive an email reminder the day before each event.'
      },
      {
        q: 'What are the community forum rules?',
        a: 'Be respectful, use neurodiversity-affirming language, no spam or self-promotion, protect privacy, and support each other. Full guidelines are displayed in the forum.'
      }
    ]
  },
  {
    category: 'Volunteering',
    questions: [
      {
        q: 'How do I become a volunteer?',
        a: 'Fill out the volunteer application form on our Volunteer page. All volunteers must consent to a background check as we work with vulnerable individuals and children.'
      },
      {
        q: 'What kind of volunteer opportunities are available?',
        a: 'Volunteers help with events, administrative tasks, social media, community support, and more. We will match your skills and availability to our needs.'
      }
    ]
  },
  {
    category: 'Training & Support',
    questions: [
      {
        q: 'Do you offer training for organisations?',
        a: 'Yes! We offer autism acceptance training for schools, workplaces, and organisations. Visit our Training page or contact us to inquire.'
      },
      {
        q: 'How do I get support if I am in crisis?',
        a: 'Visit our Crisis Support page for helpline numbers and resources. If you are in immediate danger, call emergency services (911).'
      }
    ]
  }
];

export default function FAQs() {
  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="bg-gradient-to-br from-primary/5 via-background to-secondary/5 py-12">
        <div className="container-main">
          <div className="max-w-3xl">
            <Badge className="mb-4">Help Center</Badge>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Frequently Asked Questions
            </h1>
            <p className="text-lg text-muted-foreground">
              Find answers to common questions about the Hub, our services, and how to get involved.
            </p>
          </div>
        </div>
      </div>

      <div className="container-main py-12">
        <div className="max-w-3xl mx-auto space-y-8">
          {faqs.map((category, i) => (
            <Card key={i} className="card-base">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <HelpCircle className="h-5 w-5 text-primary" />
                  {category.category}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Accordion type="single" collapsible className="w-full">
                  {category.questions.map((item, j) => (
                    <AccordionItem key={j} value={`${i}-${j}`}>
                      <AccordionTrigger className="text-left">
                        {item.q}
                      </AccordionTrigger>
                      <AccordionContent className="text-muted-foreground">
                        {item.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </CardContent>
            </Card>
          ))}

          {/* Contact */}
          <Card className="bg-muted/30">
            <CardContent className="p-8 text-center">
              <Mail className="h-12 w-12 mx-auto text-primary mb-4" />
              <h2 className="text-xl font-bold mb-2">Still have questions?</h2>
              <p className="text-muted-foreground mb-4">
                We&apos;re here to help! Reach out to us directly.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <a href="mailto:hello@autismacceptance.ky" className="text-primary hover:underline">
                  hello@autismacceptance.ky
                </a>
                <span className="text-muted-foreground">or</span>
                <a href="mailto:admin@autismacceptance.ky" className="text-primary hover:underline">
                  admin@autismacceptance.ky
                </a>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
